<template>
  <div
    class="shipment-search-results elevated"
    @mouseover="checkHover($event)"
    @mouseout="checkHover($event)"
    @mouseup="checkTextSelection"
  >
    <ContextMenu
      v-if="!readonly"
      ref="contextMenu"
      :model="contextMenuModel"
      @hide="removeLastSelection"
    />
    <SkeletonDataTable
      :key="dataTableKey"
      v-model:selection="internalSelection"
      table-class="p-datatable-sm"
      :value="results.data?.content"
      :loading="results.loading"
      :number-of-rows="5"
      :first="results.data?.pageable?.offset ?? 0"
      :rows="shipmentSearchCriteria.pageSize"
      :total-records="results.data?.totalElements"
      :sort-field="shipmentSearchCriteria.sortField"
      :sort-order="shipmentSearchCriteria.sortOrder"
      :rows-per-page-options="[10, 20, 50]"
      paginator-template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
      data-key="id"
      responsive-layout="scroll"
      current-page-report-template="Showing {first} to {last} of {totalRecords}"
      auto-layout
      :contextmenu="!readonly"
      lazy
      paginator
      reorderable-columns
      scrollable
      scroll-height="flex"
      :selection-mode="readonly ? undefined : 'multiple'"
      @column-reorder="onTableColumnReorder"
      @page="onPageChange"
      @remove="onTableColumnRemove"
      @row-contextmenu="showContextMenu"
      @sort="emit('sort', $event)"
    >
      <template #empty>
        {{
          t('common.noParamMatchingSearch', {
            param: t('shipments.shipments', 2),
          })
        }}
      </template>

      <Column
        v-if="!readonly"
        :reorderable-column="false"
        selection-mode="multiple"
        header-style="width: 3em"
        frozen
      />

      <RygenColumn
        key="id"
        field="id"
        :header="t('common.id')"
        :reorderable-column="false"
        :removable-sort="false"
        sortable
        frozen
      >
        <template #body="{ data }">
          <!-- When readonly the selection column is gone; keeping the id cell at least the
               checkbox height preserves the same row height as the selectable table. -->
          <div :class="{ 'shipment-id-cell--readonly': readonly }">
            <router-link
              :to="{
                name: 'shipmentDetail',
                params: { shipmentId: data.id },
                query: { search: searchParams },
              }"
            >
              {{ data.id }}
            </router-link>
          </div>
        </template>
      </RygenColumn>

      <RygenColumn
        v-for="col of internalColumns"
        :key="col.key"
        :field="col.field"
        :header="col.header"
        :class="col.class"
        :sortable="col.sortable"
        removable
      >
        <template v-if="col.body" #body="{ data, index }">
          <component
            :is="col.body"
            :data="data"
            :index="index"
            :search="searchParams"
            type="SHIPMENT"
          />
        </template>
      </RygenColumn>

      <ColumnSelectActionHeader
        key="columnSelectAction"
        v-model:visible="isDrawerVisible"
      />

      <!--paginatorstart template needed to balance paginatorend and paginatortemplate in the middle-->
      <template #paginatorstart></template>
      <template #paginatorend>
        <RygenButton
          mode="secondary"
          icon="pi pi-refresh"
          :aria-label="t('common.refresh')"
          @click="emit('refresh-no-cache')"
        />
      </template>
    </SkeletonDataTable>

    <ColumnSelectDrawer
      v-model:visible="isDrawerVisible"
      :columns="internalColumns"
      :options="columnOptions"
      @add="onColumnAdd"
      @add-all="onColumnsAdd"
      @column-reorder="onColumnReorder"
      @remove="onColumnRemove"
      @remove-all="onColumnsRemove"
    />
  </div>
</template>

<script lang="ts" setup>
import isEqual from 'lodash/isEqual';
import type { PropType, Ref } from 'vue';
import { computed, ref, toRefs, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useDataTableColumns } from '@rygen/rygen-ui/composables';
import {
  ActionCommand,
  ColumnSpecification,
  Loadable,
  PrimeVueReorderEvent,
} from '@rygen/rygen-ui/model';
import { stringify } from '@rygen/rygen-ui/util';

import { useShipmentBatchAction } from '@/composables';
import {
  SHIPMENT_COLUMNS,
  ShipmentAction,
  ShipmentSearchCriteria,
  ShipmentSearchData,
  ShipmentSearchResult,
} from '@/model';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import ColumnSelectDrawer from '@rygen/rygen-ui/components/search/ColumnSelectDrawer.vue';
import ColumnSelectActionHeader from '@rygen/rygen-ui/components/ui/ColumnSelectActionHeader.vue';
import RygenColumn from '@rygen/rygen-ui/components/ui/RygenColumn.vue';
import SkeletonDataTable from '@rygen/rygen-ui/components/ui/SkeletonDataTable.vue';
import Column from 'primevue/column';
import ContextMenu from 'primevue/contextmenu';
import { DataTablePageEvent, DataTableSortEvent } from 'primevue/datatable';

// Non-reorderable leading columns before the ColumnSpecification loop: the frozen id
// column always, plus the frozen selection column when not readonly.
const NON_REORDERABLE_COLUMNS_OFFSET = 1;

const props = defineProps({
  results: {
    type: Object as PropType<Loadable<ShipmentSearchResult>>,
    default: () => ({ loading: false, data: new ShipmentSearchResult() }),
  },
  selection: {
    type: Array as PropType<ShipmentSearchData[]>,
    default: () => [],
  },
  shipmentSearchCriteria: {
    type: ShipmentSearchCriteria,
    required: true,
  },
  columns: {
    type: Array as PropType<ColumnSpecification[]>,
    default: () => SHIPMENT_COLUMNS,
  },
  columnOptions: {
    type: Array as PropType<ColumnSpecification[]>,
    default: () => SHIPMENT_COLUMNS,
  },
  handlingCommand: {
    type: Boolean,
    default: () => false,
  },
  // When true, hides row selection + the batch-action context menu (read-only contexts
  // like the manifest detail page)
  readonly: {
    type: Boolean,
    default: () => false,
  },
});
const {
  results,
  shipmentSearchCriteria,
  selection,
  columns,
  handlingCommand,
  readonly,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'command', command: ActionCommand<ShipmentAction>): void;
  (event: 'edited-columns', newColumns: ColumnSpecification[]): void;
  (event: 'page', payload: DataTablePageEvent): void;
  (event: 'refresh-no-cache'): void;
  (event: 'sort', payload: DataTableSortEvent): void;
  (event: 'update:columns', newVal: ColumnSpecification[]): void;
  (event: 'update:selection', newVal: ShipmentSearchData[]): void;
}>();

const contextMenu = useTemplateRef('contextMenu');

const { t } = useI18n();

const internalColumns: Ref<ColumnSpecification[]> = ref(columns.value);
const {
  addColumn,
  addColumns,
  dataTableKey,
  removeColumn,
  removeColumns,
  reorderColumn,
} = useDataTableColumns(internalColumns);

const internalSelection = ref<ShipmentSearchData[]>([...selection.value]);
const isHandlingCommand = ref<boolean>(handlingCommand.value);
const emitCommand = (command: ActionCommand<ShipmentAction>) => {
  isHandlingCommand.value = true;
  emit('command', command);
};
const { availableActions } = useShipmentBatchAction(
  internalSelection,
  emitCommand,
);

const isDrawerVisible = ref(false);
const isRightClickSelection = ref(false);
const isTextSelectedOrLinkHovered = ref(false);
const hoveredElement = ref<HTMLElement | null>(null);

const searchParams = computed(() => stringify(shipmentSearchCriteria.value));

const contextMenuModel = computed(() => {
  return availableActions.value.length
    ? availableActions.value
    : [
        {
          label: t('batchAction.noActionForParam', {
            param: t('shipments.shipments', internalSelection.value.length),
          }),
        },
      ];
});

const onTableColumnRemove = (colField: string) => {
  const column = internalColumns.value.find((col) => col.field === colField);

  if (column) {
    onColumnRemove(column);
  } else {
    console.error(
      colField,
      ' not found in internalColumns: ',
      internalColumns.value,
    );
  }
};

const onTableColumnReorder = (event: PrimeVueReorderEvent) => {
  const offset = readonly.value
    ? NON_REORDERABLE_COLUMNS_OFFSET
    : NON_REORDERABLE_COLUMNS_OFFSET + 1;
  const adjustedEvent: PrimeVueReorderEvent = {
    ...event,
    dragIndex: event.dragIndex - offset,
    dropIndex: event.dropIndex - offset,
  };

  return onColumnReorder(adjustedEvent, true);
};

const onColumnAdd = (column: ColumnSpecification) => {
  addColumn(column);
  emit('edited-columns', internalColumns.value);
};

const onColumnsAdd = (columns: ColumnSpecification[]) => {
  addColumns(columns);
  emit('edited-columns', internalColumns.value);
};

const onColumnRemove = (column: ColumnSpecification) => {
  removeColumn(column);
  emit('edited-columns', internalColumns.value);
};

const onColumnsRemove = (columns: ColumnSpecification[]) => {
  removeColumns(columns);
  emit('edited-columns', internalColumns.value);
};

const onColumnReorder = (
  event: PrimeVueReorderEvent,
  isTableUpdate = false,
) => {
  // Only SHIPMENT_COLUMNS defined columns should be reorderable to remain sync with ColumnSelectDrawer.
  reorderColumn(event, isTableUpdate);
  emit('edited-columns', internalColumns.value);
};

const showContextMenu = ({
  data,
  originalEvent,
}: {
  originalEvent: Event;
  data: ShipmentSearchData;
}) => {
  if (isTextSelectedOrLinkHovered.value) {
    return;
  }

  if (!internalSelection.value.length) {
    internalSelection.value.push(data);
    isRightClickSelection.value = true;
  }

  originalEvent.preventDefault();
  contextMenu.value?.show?.(originalEvent);
};

const removeLastSelection = () => {
  if (isRightClickSelection.value && !isHandlingCommand.value) {
    internalSelection.value.pop();
    isRightClickSelection.value = false;
  }
};

const checkTextSelection = () => {
  const selection = window.getSelection()?.toString();
  isTextSelectedOrLinkHovered.value = Boolean(selection);
};

const checkHover = (event: any) => {
  if (event.type === 'mouseover') {
    hoveredElement.value = event.target;
  } else if (event.type === 'mouseout') {
    hoveredElement.value = null;
  }

  isTextSelectedOrLinkHovered.value =
    hoveredElement.value?.tagName === 'A' ||
    Boolean(window.getSelection()?.toString());
};

const onPageChange = (event: DataTablePageEvent) => {
  internalSelection.value = [];
  emit('page', event);
};

watch(
  columns,
  (newVal: ColumnSpecification[]) => {
    internalColumns.value = newVal;
  },
  { deep: true, immediate: true },
);

watch(internalColumns, (newVal: ColumnSpecification[]) => {
  emit('update:columns', newVal);
});

watch(
  selection,
  (newVal: ShipmentSearchData[]) => {
    internalSelection.value = [...newVal];
  },
  { deep: true, immediate: true },
);

watch(
  internalSelection,
  (newVal: ShipmentSearchData[]) => {
    if (!isEqual(selection.value, newVal)) {
      emit('update:selection', newVal);
    }
  },
  { deep: true },
);

watch(handlingCommand, (newVal: boolean) => {
  isHandlingCommand.value = newVal;
});
</script>

<style>
.shipment-search-results {
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .data-table-container {
    max-width: none;

    .shipment-id-cell--readonly {
      display: flex;
      align-items: center;
      min-height: var(--p-checkbox-height);
    }
  }
}
</style>
